import { AuthRepository } from '../api/repository/auth';
import { normalizeError } from '../api/core/errors';
import { useQltAuthStore } from '../stores/qlt';
import { startHeartbeat } from './heartbeatLoop';
import type { Heartbeat } from './heartbeatLoop';

interface RouterLike {
  state: { location: { pathname: string } };
  subscribe: (listener: (state: { location: { pathname: string } }) => void) => () => void;
}

const browserScheduler = {
  setTimeout: (fn: () => void, ms: number) => window.setTimeout(fn, ms),
  clearTimeout: (handle: unknown) => window.clearTimeout(handle as number),
};

const pageVisible = () => document.visibilityState !== 'hidden';

// 401/403 ends the session; anything else (offline, 5xx) leaves it alone.
async function probeSession(): Promise<void> {
  const jwt = useQltAuthStore.getState().jwt;
  if (!jwt) return;
  try {
    await AuthRepository.me();
  } catch (error) {
    const kind = normalizeError(error).kind;
    // a 401 already signed out through the client; only act if this probe's session is still current
    if ((kind === 'unauthorized' || kind === 'forbidden') && useQltAuthStore.getState().jwt === jwt) {
      useQltAuthStore.getState().signOutRejected();
    }
  }
}

/** Lets the teacher's phone end this board's session: polls /users/me while signed in and visible. */
export function installSessionHeartbeat(router: RouterLike): void {
  let beat: Heartbeat | null = null;

  function sync(): void {
    const auth = useQltAuthStore.getState();
    const run = Boolean(auth.jwt) && !auth.hydrating && router.state.location.pathname !== '/login';
    if (run && !beat) beat = startHeartbeat({ probe: probeSession, isVisible: pageVisible, scheduler: browserScheduler });
    if (!run && beat) {
      beat.stop();
      beat = null;
    }
  }

  useQltAuthStore.subscribe(sync);
  router.subscribe(sync);
  document.addEventListener('visibilitychange', () => {
    if (beat) beat.visibilityChanged();
  }, false);
  sync();
}
